<template>
  <div>
    <h2>予約一覧</h2>
    <ul :class="$style.list">
      <li v-for="node in nodes" :key="node.guid" :class="$style.list__item">
        <router-link
          :class="$style.listItem"
          :to="{
            name: 'calendarSettingBooking',
            params: {
              bookingGUID: node.guid,
              entranceGUID: route.params.entranceGUID,
            },
          }"
        >
          <div :class="$style.listItem__date">
            <div>
              {{ dateStringForDisplay(node) }}
            </div>
          </div>
          <DolphinChip
            v-if="node.cancelReason"
            :class="$style.listItem__cancelChip"
          >
            キャンセル済み
          </DolphinChip>
          <div :class="$style.listItem__summary">{{ node.summary }}</div>
          <div :class="$style.listItem__displayName">
            {{ node.assignedStaff.displayName }}
          </div>
        </router-link>
      </li>
    </ul>
    <FlatButton
      v-if="hasLoadMore"
      :class="$style.loadMoreButton"
      variant="secondary"
      @click="onClickLoadMore"
      :disabled="isLoadingMore"
    >
      もっと見る
    </FlatButton>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import DolphinChip from '@/components/atoms/DolphinChip.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useAlert } from '@/libs/alertPortal'
import { useFetchCalendarBookingsByCalendarGuidQuery } from './CalendarBookings.generated.ts'
import { dateStringForDisplay } from './booking.ts'

const route = useRoute('calendarSetting')

const props = defineProps<{
  calendarGUID: string
}>()

const {
  fetchMore: calendarBookingsQueryFetchMore,
  result: calendarBookingsQueryResult,
} = useFetchCalendarBookingsByCalendarGuidQuery(
  {
    guid: props.calendarGUID,
    first: 20,
  },
  { fetchPolicy: 'no-cache' }
)

const nodes = computed(() =>
  calendarBookingsQueryResult.value?.bookingsByCalendarGUID.edges.map(
    e => e.node
  )
)
const hasLoadMore = computed(
  () =>
    calendarBookingsQueryResult.value?.bookingsByCalendarGUID.pageInfo
      .hasNextPage
)
const isLoadingMore = ref(false)

const alert = useAlert()

const onClickLoadMore = async () => {
  if (
    calendarBookingsQueryResult.value?.bookingsByCalendarGUID.pageInfo
      .hasNextPage
  ) {
    isLoadingMore.value = true
    try {
      await calendarBookingsQueryFetchMore({
        variables: {
          after:
            calendarBookingsQueryResult.value?.bookingsByCalendarGUID.pageInfo
              .endCursor,
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newEdges = fetchMoreResult?.bookingsByCalendarGUID.edges
          const pageInfo = fetchMoreResult?.bookingsByCalendarGUID.pageInfo

          return newEdges?.length && pageInfo
            ? {
                ...previousResult,
                bookingsByCalendarGUID: {
                  ...previousResult.bookingsByCalendarGUID,
                  edges: [
                    ...previousResult.bookingsByCalendarGUID.edges,
                    ...newEdges,
                  ],
                  pageInfo,
                },
              }
            : previousResult
        },
      })
    } catch (e) {
      console.error(e)
      alert.error('エラーが発生しました。再度お試しください。')
    } finally {
      isLoadingMore.value = false
    }
  }
}
</script>

<style lang="scss" module>
.list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 8px;

  &__item {
    padding: 16px;
    border: 1px solid $color-border-low-emphasis;
    border-radius: $border-radius-v2-base;

    &:hover {
      background-color: $color-surface-accent-primary-light;
    }
  }
}

.listItem {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  color: $color-text-and-object-high-emphasis;

  &__date {
    flex-shrink: 0;
  }

  &__cancelChip {
    flex-shrink: 0;
    color: $color-text-and-object-high-emphasis-inverse;
    background-color: $color-label-gray;
  }

  &__summary {
    flex-grow: 1;
  }

  &__displayName {
    flex-shrink: 0;
    max-width: 100px;
    overflow-wrap: anywhere;
  }
}

.loadMoreButton {
  margin-top: 16px;
}
</style>
